import React from 'react';

import { PatternFormat } from 'react-number-format';
import { TextField } from '@mui/material';
import {
  Control,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
} from 'react-hook-form';

type AgeInputProps<T extends FieldValues, TName extends Path<T>> = {
  control: Control<T>;
  startFieldProps: Omit<UseControllerProps<T, TName>, 'control'>;
  endFieldProps: Omit<UseControllerProps<T, TName>, 'control'>;
};

const AgeInput = <T extends FieldValues, TName extends Path<T>>({
  control,
  startFieldProps,
  endFieldProps,
}: AgeInputProps<T, TName>) => {
  const {
    field: fieldStart,
    fieldState: { error: errorStart },
  } = useController({ control, ...startFieldProps });

  const {
    field: fieldEnd,
    fieldState: { error: errorEnd },
  } = useController({ control, ...endFieldProps });

  const rawValue = `${fieldStart.value ? String(fieldStart.value) : ''}${fieldEnd.value ? String(fieldEnd.value) : ''}`;

  const handleValueChange = (values: { value: string }) => {
    const { value } = values;

    if (value.length <= 2) {
      fieldStart.onChange(value ? Number(value) : null);
      fieldEnd.onChange(null);
    } else {
      fieldStart.onChange(Number(value.slice(0, 2)));
      fieldEnd.onChange(Number(value.slice(2)) || null);
    }
  };

  const error = errorStart || errorEnd;

  return (
    <PatternFormat
      format="##-##"
      allowEmptyFormatting={false}
      customInput={TextField}
      value={rawValue}
      onValueChange={handleValueChange}
      variant="outlined"
      fullWidth
      label="Age"
      placeholder="XX-XX"
      size="small"
      error={!!error}
      helperText={error?.message}
      slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' } }}
      inputRef={(el: HTMLInputElement | null) => {
        fieldStart.ref(el);
        fieldEnd.ref(el);
      }}
    />
  );
};

export { AgeInput };
